/**
 * OrderKyc — customer-facing KYC document upload page.
 *
 * Mounted at /dashboard/orders/:orderId/kyc. Visible when an admin has put
 * the order into `awaiting_documents` status. After both files are uploaded
 * the order auto-transitions to `documents_uploaded` (admin reviews).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Upload, ShieldCheck, FileText, CheckCircle2, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function OrderKyc() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [files, setFiles] = useState({ id_proof: null, address_proof: null });
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [existing, setExisting] = useState([]);

  const load = async () => {
    try {
      const { data: o } = await api.get(`/orders/${orderId}`);
      setOrder(o);
      const { data: list } = await api.get(`/orders/${orderId}/kyc/files`);
      setExisting(list.files || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order not found");
    } finally { setLoaded(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const onFile = (key) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error(`${f.name}: only PDF, JPG, PNG are allowed`);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`${f.name}: max size is 5 MB`);
      e.target.value = "";
      return;
    }
    setFiles((p) => ({ ...p, [key]: f }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.id_proof || !files.address_proof) {
      toast.error("Both ID Proof and Address Proof are required");
      return;
    }
    const fd = new FormData();
    fd.append("id_proof", files.id_proof);
    fd.append("address_proof", files.address_proof);
    if (note) fd.append("note", note);
    setUploading(true);
    try {
      await api.post(`/orders/${orderId}/kyc-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documents uploaded — our team will review within 1 business day.");
      await load();
      setFiles({ id_proof: null, address_proof: null });
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!loaded) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-400" size={28}/></div>;
  }
  if (!order) {
    return (
      <div className="card p-8 text-center" data-testid="kyc-no-order">
        <AlertTriangle className="mx-auto text-rose-400 mb-3" size={32}/>
        <h2 className="text-xl font-bold text-white">Order not found</h2>
        <Link to="/dashboard/orders" className="btn-ghost mt-4 inline-flex"><ArrowLeft size={14}/> Back to orders</Link>
      </div>
    );
  }

  const status = order.status;
  const canUpload = status === "awaiting_documents" || status === "documents_uploaded";
  const reason = order.documents_request_reason;

  return (
    <div className="space-y-5" data-testid="order-kyc-page">
      <div>
        <Link to="/dashboard/orders" className="text-sm text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1.5"><ArrowLeft size={13}/> Back to orders</Link>
        <h1 className="text-3xl font-extrabold text-white mt-2" style={{fontFamily:"Outfit"}}>Verification documents</h1>
        <p className="text-slate-400 text-sm mt-1">
          Order <strong className="text-white font-mono">#{order.id.slice(0,8).toUpperCase()}</strong> · Status:
          <span className="ml-1 text-amber-300">{status.replace("_", " ")}</span>
        </p>
      </div>

      {/* Admin reason banner */}
      {reason && (
        <div className="card p-4 border-l-4 border-amber-400 bg-amber-500/5" data-testid="kyc-reason-banner">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={18}/>
            <div>
              <div className="text-white font-semibold text-sm">Action required</div>
              <p className="text-slate-300 text-sm mt-1">{reason}</p>
            </div>
          </div>
        </div>
      )}

      {!canUpload && (
        <div className="card p-6 text-center" data-testid="kyc-not-required">
          <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={32}/>
          <h2 className="text-lg font-bold text-white">No documents required right now</h2>
          <p className="text-slate-400 text-sm mt-1">This order does not currently require document verification.</p>
        </div>
      )}

      {canUpload && (
        <form onSubmit={submit} className="card p-6 space-y-5" data-testid="kyc-upload-form">
          <div className="flex items-center gap-2 text-blue-300">
            <ShieldCheck size={18}/>
            <h2 className="text-lg font-bold">Upload verification documents</h2>
          </div>
          <p className="text-xs text-slate-400 -mt-3">PDF, JPG or PNG. Max 5 MB per file.</p>

          <div className="grid md:grid-cols-2 gap-4">
            <FilePicker
              label="ID Proof"
              hint="Aadhaar / PAN / Passport / Driving Licence"
              file={files.id_proof}
              onChange={onFile("id_proof")}
              testid="kyc-id-proof"
            />
            <FilePicker
              label="Address Proof"
              hint="Utility bill / Bank statement / Rent agreement"
              file={files.address_proof}
              onChange={onFile("address_proof")}
              testid="kyc-address-proof"
            />
          </div>

          <div>
            <label className="label">Note (optional)</label>
            <textarea
              value={note} onChange={(e)=>setNote(e.target.value)}
              rows={2}
              placeholder="Anything you'd like the reviewer to know"
              className="input w-full"
              data-testid="kyc-note"
            />
          </div>

          <button
            type="submit"
            disabled={uploading || !files.id_proof || !files.address_proof}
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
            data-testid="kyc-submit-btn"
          >
            {uploading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
            {existing.length > 0 ? "Re-upload documents" : "Upload documents"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            Files are stored privately and shared only with the Optivize verification team.
          </p>
        </form>
      )}

      {/* Already uploaded list */}
      {existing.length > 0 && (
        <div className="card p-5" data-testid="kyc-existing">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><FileText size={15} className="text-emerald-400"/> Previously uploaded</h3>
          <ul className="divide-y divide-white/5 text-sm">
            {existing.map((f) => (
              <li key={f.id} className="py-2.5 flex items-center justify-between">
                <div>
                  <div className="text-white">{f.doc_type === "id_proof" ? "ID Proof" : "Address Proof"}</div>
                  <div className="text-xs text-slate-400">{f.original_filename} · {(f.size/1024).toFixed(1)} KB · {f.uploaded_at?.slice(0,16).replace("T"," ")}</div>
                </div>
                <span className="text-emerald-300 text-xs inline-flex items-center gap-1"><CheckCircle2 size={13}/> Received</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FilePicker({ label, hint, file, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="label">{label} <span className="text-rose-400">*</span></label>
      <label
        htmlFor={testid}
        className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl px-4 py-6 cursor-pointer transition ${
          file ? "border-emerald-500/60 bg-emerald-500/5" : "border-slate-700 hover:border-blue-500 hover:bg-blue-500/5"
        }`}
        data-testid={`${testid}-drop`}
      >
        {file ? (
          <>
            <CheckCircle2 size={22} className="text-emerald-400 mb-1.5"/>
            <span className="text-emerald-200 text-sm font-semibold truncate max-w-full">{file.name}</span>
            <span className="text-slate-400 text-xs mt-0.5">{(file.size/1024).toFixed(1)} KB · click to replace</span>
          </>
        ) : (
          <>
            <Upload size={22} className="text-slate-400 mb-1.5"/>
            <span className="text-slate-200 text-sm font-semibold">Click to select file</span>
            <span className="text-slate-500 text-xs mt-0.5">{hint}</span>
          </>
        )}
      </label>
      <input
        id={testid}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        onChange={onChange}
        className="hidden"
        data-testid={testid}
      />
    </div>
  );
}
